import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Table,
  Tooltip,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure
} from '@nextui-org/react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../lib/firebase';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { maskEmail, maskHandle } from '../lib/mask';
import { applySort } from '../lib/sort';
import AuditLog from '../components/AuditLog';
import RobloxIdLookup from '../components/RobloxIdLookup';
// Idioma del correo de restablecer la contraseña: el mismo módulo que la app.
import '../../../renderer/auth/firebaseLocale.js';

// Usuarios en live: polling a GET /api/backoffice/live (D1 live_presence).
// Antes era un onSnapshot sobre liveCreators en Firestore y cada heartbeat
// de cada streamer (30 s) costaba una lectura por backoffice abierto.
const LIVE_REFRESH_ACTIVE_MS = 30 * 1000;
const LIVE_REFRESH_IDLE_MS = 3 * 60 * 1000;
const EXPORT_PAGE_SIZE = '1000';
const PAGE_SIZE_OPTIONS = ['25', '50', '100'];
const AMBASSADOR_LANGUAGE_OPTIONS = ['ES', 'EN', 'PT'];

const PLAN_FILTERS = [
  ['all', 'Todos'],
  ['free', 'Free'],
  ['premium', 'Premium'],
  ['premium_paying', 'Premium pago'],
  ['premium_trial', 'Premium trial'],
  ['founders', 'Founders'],
  ['performance', 'Performance'],
  ['internal', 'Internos'],
  ['ambassador', 'Embajadores'],
  ['marketing', 'Marketing'],
  ['other', 'Otros']
];

const USER_PLAN_OPTIONS = [
  'free',
  'premium_monthly',
  'premium_3months',
  'premium_year',
  'performance_monthly',
  'performance_3months',
  'performance_year',
  'premium_internal',
  'premium_ambassador',
  'premium_marketing',
  'founders_premium'
];

const SUBSCRIPTION_FILTERS = [
  ['all', 'Todas'],
  ['active', 'Activa'],
  ['trialing', 'Trial'],
  ['canceling', 'Cancela al final'],
  ['inactive', 'Inactiva'],
  ['canceled', 'Cancelada']
];

const PLAN_LABELS = Object.fromEntries(PLAN_FILTERS);
const SUBSCRIPTION_LABELS = Object.fromEntries(SUBSCRIPTION_FILTERS);
// Cards de "Totales por plan": Premium separado en pagando vs trial (no
// pueden ir juntos) y sin Embajadores (para eso está el desplegable de plan).
const PLAN_COUNT_KEYS = ['free', 'premium_paying', 'premium_trial', 'founders', 'performance'];
const SUBSCRIPTION_COUNT_KEYS = SUBSCRIPTION_FILTERS.filter(([key]) => key !== 'all').map(([key]) => key);
// Color de cada métrica (punto junto al título y subrayado cuando filtra).
const PLAN_COUNT_META = {
  free: { dot: 'bg-default-400' },
  premium: { dot: 'bg-primary' },
  premium_paying: { dot: 'bg-primary' },
  premium_trial: { dot: 'bg-warning' },
  founders: { dot: 'bg-secondary' },
  performance: { dot: 'bg-success' }
};
const SUBSCRIPTION_COUNT_META = {
  active: { dot: 'bg-success' },
  trialing: { dot: 'bg-secondary' },
  canceling: { dot: 'bg-warning' },
  inactive: { dot: 'bg-default-400' },
  canceled: { dot: 'bg-danger' }
};

const USER_COLUMNS = [
  { key: 'email', label: 'Email' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'plan', label: 'Plan' },
  { key: 'subscription', label: 'Estado' },
  { key: 'subscriptionDate', label: 'Suscripción' },
  { key: 'cancellationDate', label: 'Cancelación' },
  { key: 'stripeCustomerId', label: 'Customer ID' },
  { key: 'stripeSubscriptionId', label: 'Subscription ID' },
  { key: 'appVersion', label: 'Version app' },
  { key: 'appVersionLastSeenAt', label: 'Ultimo inicio' },
  { key: 'language', label: 'Idioma' },
  { key: 'createdAt', label: 'Registro' },
  { key: 'planEnd', label: 'Fin' },
  { key: 'founderNumber', label: 'Founder #' },
  { key: 'ambassadorNumber', label: 'Ambassador' },
  { key: 'admin', label: 'Rol' }
];

// Tabla plana al estilo del Dashboard: sin caja ni esquinas redondeadas,
// cabecera con una línea inferior y filas alternando dos tonos del mismo
// gris, separadas por una línea fina.
const FLAT_TABLE_CLASSNAMES = {
  th: 'rounded-none bg-transparent border-b border-default-300 text-[11px] uppercase tracking-wider text-default-500 first:rounded-none last:rounded-none',
  tr: 'border-b border-default-200/70 odd:bg-default-100/40 even:bg-transparent transition-colors hover:bg-default-200/40',
  td: 'first:rounded-none last:rounded-none'
};

const DEFAULT_VISIBLE_USER_COLUMNS = new Set(USER_COLUMNS.map((column) => column.key));

const LIVE_COLUMNS = [
  { key: 'tiktok', label: 'TikTok' },
  { key: 'viewerCount', label: 'Viewers' },
  { key: 'lastUpdated', label: 'Actualizado' },
  { key: 'uid', label: 'UID' }
];

const LANGUAGE_FILTER_OPTIONS = [
  ['all', 'Todos'],
  ['ES', 'ES'],
  ['EN', 'EN'],
  ['PT', 'PT'],
  ['FR', 'FR'],
  ['DE', 'DE'],
  ['RU', 'RU'],
  ['ZH', 'ZH'],
  ['JA', 'JA'],
  ['HI', 'HI']
];

const ROLE_FILTER_OPTIONS = [
  ['all', 'Todos'],
  ['admin', 'Solo ADMIN'],
  ['viewer', 'Solo VIEWER'],
  ['none', 'Sin rol']
];

const EMPTY_ADVANCED_FILTERS = {
  createdFrom: '',
  createdTo: '',
  endFrom: '',
  endTo: '',
  cancelFrom: '',
  cancelTo: '',
  lastSeenFrom: '',
  lastSeenTo: '',
  appVersion: '',
  language: 'all',
  role: 'all'
};

// Filtros de plan y estado como listas (varios valores = OR). Vacía = todos.
function toggleFilterValue(list, key) {
  return list.includes(key) ? list.filter((item) => item !== key) : [...list, key];
}

function filterParam(list) {
  return list.length ? list.join(',') : 'all';
}

function countActiveAdvancedFilters(filters) {
  let count = 0;
  if (filters.createdFrom || filters.createdTo) count++;
  if (filters.endFrom || filters.endTo) count++;
  if (filters.cancelFrom || filters.cancelTo) count++;
  if (filters.lastSeenFrom || filters.lastSeenTo) count++;
  if (filters.appVersion.trim()) count++;
  if (filters.language !== 'all') count++;
  if (filters.role !== 'all') count++;
  return count;
}

function tsToMillis(value) {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value);
    if (Number.isFinite(n) && n > 1000000000) return n;
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d.getTime() : 0;
  }
  if (value?.__type === 'timestamp' && value.value) return tsToMillis(value.value);
  if (typeof value?._seconds === 'number') return value._seconds * 1000;
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return 0;
}

function formatDate(value) {
  const ms = tsToMillis(value);
  return ms ? new Date(ms).toLocaleDateString() : '-';
}

function formatDateTime(value) {
  const ms = tsToMillis(value);
  return ms ? new Date(ms).toLocaleString() : '-';
}

function effectivePlanEnd(user) {
  return user?.stripeCurrentPeriodEnd || user?.planEnd || user?.founderCurrentPeriodEnd || null;
}

function subscriptionDate(user) {
  return (
    user?.stripeSubscriptionCreated ||
    user?.subscriptionCreatedAt ||
    user?.subscriptionStart ||
    user?.stripeCurrentPeriodStart ||
    user?.planStart ||
    user?.founderPurchasedAt ||
    user?.founderAssignedAt ||
    null
  );
}

function cancellationDate(user) {
  if (!['canceling', 'canceled'].includes(user?.subscriptionState)) return null;
  return (
    user?.stripeCanceledAt ||
    user?.stripeCancellationDate ||
    user?.subscriptionCanceledAt ||
    user?.cancellationDate ||
    user?.canceledAt ||
    user?.cancelledAt ||
    user?.founderCanceledAt ||
    user?.founder?.canceledAt ||
    null
  );
}

function formatPlan(plan) {
  if (!plan) return 'free';
  const labels = {
    premium_internal: 'Premium interno',
    premium_ambassador: 'Premium embajador',
    premium_marketing: 'Premium Marketing',
    premium_trial: 'Premium prueba',
    founders_premium: 'Founder Premium'
  };
  return labels[plan] || String(plan).replace(/_/g, ' ').toLowerCase();
}

function normalizeLanguage(value) {
  const lang = String(value || '').trim().toLowerCase();
  if (lang.startsWith('es')) return 'ES';
  if (lang.startsWith('en')) return 'EN';
  return lang ? lang.toUpperCase() : '-';
}

function formatAppVersion(value) {
  const version = String(value || '').trim();
  return version ? `v${version}` : '-';
}

function founderRawValue(user) {
  return user?.founder?.number ?? user?.founderNumber ?? user?.founderNo ?? user?.foundersNumber ?? null;
}

function ambassadorInfo(user) {
  if (!user?.ambassador || typeof user.ambassador !== 'object') return null;
  const ambassador = user.ambassador;
  const language = String(
    ambassador.language ||
    ambassador.region ||
    ''
  ).trim().toUpperCase();
  if (!language || ambassador.active === false) return null;
  return {
    language,
    region: language,
    number: 0,
    paddedNumber: '0000'
  };
}

function ambassadorLabel(user) {
  const ambassador = ambassadorInfo(user);
  return ambassador ? `${ambassador.language} #0000` : '';
}

function stripeCustomerIdOf(user) {
  return user?.stripeCustomerId || user?.founder?.stripeCustomerId || user?.founderStripeCustomerId || null;
}

function stripeSubscriptionIdOf(user) {
  return user?.stripeSubscriptionId || user?.founder?.stripeSubscriptionId || user?.founderStripeSubscriptionId || null;
}

function stripeDashboardUrl(user) {
  const customerId = stripeCustomerIdOf(user);
  if (customerId) return `https://dashboard.stripe.com/customers/${encodeURIComponent(customerId)}`;
  const subscriptionId = stripeSubscriptionIdOf(user);
  if (subscriptionId) return `https://dashboard.stripe.com/subscriptions/${encodeURIComponent(subscriptionId)}`;
  return null;
}

// Composición del card "Activa" de Totales por estado: cuántos tienen
// suscripción real (Stripe/Founder) y cuántos son activos "por decreto".
function subscriptionCountDetail(key, facets) {
  if (key !== 'active') return null;
  const breakdown = facets?.activeBreakdown;
  if (!breakdown) return null;
  const stripe = Number(breakdown.stripe || 0);
  const noStripe = Number(breakdown.internal || 0) + Number(breakdown.marketing || 0)
    + Number(breakdown.ambassador || 0) + Number(breakdown.granted || 0);
  if (!stripe && !noStripe) return null;
  return {
    line: noStripe
      ? `${stripe.toLocaleString()} Stripe · ${noStripe} sin Stripe`
      : 'todas con suscripcion de Stripe',
    breakdown
  };
}

// Contenido del popover del (i) del card "Activa".
function ActiveBreakdownDetail({ breakdown, total }) {
  const rows = [
    ['Con suscripcion de Stripe o Founder', breakdown.stripe, 'bg-success'],
    ['Internos (equipo)', breakdown.internal, 'bg-default-400'],
    ['Marketing', breakdown.marketing, 'bg-primary'],
    ['Embajadores', breakdown.ambassador, 'bg-danger'],
    ['Premium regalado (fecha fin manual)', breakdown.granted, 'bg-warning']
  ];
  return (
    <div className="w-full p-4">
      <div className="text-sm font-semibold">De que se compone "Activa"</div>
      <div className="mt-1 text-xs text-default-500">
        {Number(total || 0).toLocaleString()} usuarios con acceso de pago activo ahora mismo
      </div>
      <div className="mt-3 flex flex-col gap-2">
        {rows.map(([label, value, accent]) => (
          <div key={label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${accent}`} />
              <span className="truncate text-default-600">{label}</span>
            </span>
            <span className="shrink-0 font-semibold tabular-nums">{Number(value || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] leading-4 text-default-400">
        "Stripe" significa que el usuario tiene id de suscripcion guardado; no se
        valida en vivo contra Stripe si esa suscripcion sigue cobrandose.
      </p>
    </div>
  );
}

// (i) con el detalle de una métrica: tooltip si es texto, popover si es un
// bloque (desglose de "Activa"). Vive FUERA del botón que filtra.
function FacetInfo({ text, content }) {
  const trigger = (
    <button
      type="button"
      aria-label="Mas informacion"
      className="inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center rounded-full border border-default-400 text-[9px] font-semibold leading-none text-default-500 transition hover:border-primary hover:text-primary"
    >
      i
    </button>
  );
  if (content) {
    return (
      <Popover placement="bottom">
        <PopoverTrigger>{trigger}</PopoverTrigger>
        <PopoverContent className="w-80 p-0">{content}</PopoverContent>
      </Popover>
    );
  }
  return (
    <Tooltip
      placement="bottom"
      delay={150}
      closeDelay={0}
      content={<div className="max-w-xs px-1 py-1 text-xs leading-5 text-default-600">{text}</div>}
      classNames={{ content: 'border border-default-200 bg-content1 shadow-xl' }}
    >
      {trigger}
    </Tooltip>
  );
}

// Métrica clicable (filtra la tabla), compacta para que plan y estado quepan
// en una sola línea: punto de color + etiqueta + (i) arriba, número debajo;
// subrayada con su color cuando filtra.
function FacetStat({ label, count, meta, active, onClick, info, infoContent }) {
  // Toda la zona filtra (div con rol de botón, no <button>: dentro va el (i),
  // que es otro botón y no puede anidarse). El (i) corta la propagación para
  // que abrir el detalle no active el filtro.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="group relative flex min-w-0 cursor-pointer flex-col px-3 pb-1.5 pt-0.5 outline-none transition first:pl-0 last:pr-0 hover:bg-default-100/40 focus-visible:bg-default-100/40"
    >
      {/* Subrayado del filtro activo como barra absoluta: un border-b aquí
          heredaría el color gris del divide-x del contenedor. */}
      {active ? <span className={`absolute inset-x-0 bottom-0 h-0.5 rounded-full ${meta.dot}`} /> : null}
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-default-500">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
        <span className={`truncate ${active ? 'font-semibold text-foreground' : ''}`}>{label}</span>
        {(info || infoContent) ? (
          <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <FacetInfo text={info} content={infoContent} />
          </span>
        ) : null}
      </span>
      <span className="mt-0.5 text-lg font-semibold leading-none text-foreground transition group-hover:text-primary">
        {Number(count || 0).toLocaleString()}
      </span>
    </div>
  );
}

// Grupo = título estrecho a la izquierda + sus métricas en línea.
function FacetGroup({ title, subtitle, showReset, onReset, children }) {
  return (
    <div className="flex min-w-0 items-start gap-3 px-4 first:pl-0 last:pr-0">
      <div className="w-24 shrink-0 pt-0.5">
        <div className="text-xs font-semibold leading-tight">{title}</div>
        {subtitle ? <div className="text-[10px] leading-tight text-default-400">{subtitle}</div> : null}
        {showReset ? (
          <button type="button" className="mt-0.5 text-[10px] text-primary" onClick={onReset}>
            ver todos
          </button>
        ) : null}
      </div>
      <div className="grid min-w-0 flex-1 grid-cols-5 divide-x divide-default-200">
        {children}
      </div>
    </div>
  );
}

function planCountDetail(key, facets) {
  if (key === 'premium_paying') return 'activas + cancelan a fin de periodo';
  if (key === 'premium_trial') return 'trial de Stripe o 7 dias por TikTok';
  if (key !== 'founders') return null;
  const meta = facets?.meta?.planCounts?.founders || facets?.meta?.founders || {};
  const maxNumber = Number(meta.maxNumber || 0);
  const missing = Array.isArray(meta.missingNumbers) ? meta.missingNumbers : [];
  const duplicates = Array.isArray(meta.duplicateNumbers) ? meta.duplicateNumbers : [];
  const parts = [];
  if (maxNumber > 0) parts.push(`ultimo #${maxNumber}`);
  if (missing.length > 0) {
    const visible = missing.slice(0, 4).map((number) => `#${number}`).join(', ');
    parts.push(`faltan ${visible}${missing.length > 4 ? ` +${missing.length - 4}` : ''}`);
  }
  if (duplicates.length > 0) {
    const visible = duplicates.slice(0, 3).map((number) => `#${number}`).join(', ');
    parts.push(`duplicados ${visible}${duplicates.length > 3 ? ` +${duplicates.length - 3}` : ''}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function normalizeSortText(value) {
  return String(value || '').trim().toLowerCase();
}

function roleChip(user) {
  if (user.ADMIN === true) return <Chip size="sm" color="warning" variant="flat">ADMIN</Chip>;
  if (user.ADMIN_VIEWER === true) return <Chip size="sm" variant="flat">VIEWER</Chip>;
  return <span className="text-default-400">-</span>;
}

function subscriptionChip(user) {
  const state = user.subscriptionState || 'inactive';
  const color = {
    active: 'success',
    trialing: 'secondary',
    canceling: 'warning',
    canceled: 'danger',
    inactive: 'default'
  }[state] || 'default';
  const label = {
    active: 'activa',
    trialing: 'trial',
    canceling: 'fin periodo',
    canceled: 'cancelada',
    inactive: 'inactiva'
  }[state] || state;
  return <Chip size="sm" color={color} variant="flat">{label}</Chip>;
}

function subscriptionText(user) {
  const state = user?.subscriptionState || 'inactive';
  return {
    active: 'activa',
    trialing: 'trial',
    canceling: 'fin periodo',
    canceled: 'cancelada',
    inactive: 'inactiva'
  }[state] || state;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\r?\n/g, '<br/>');
}

function escapeCsv(value) {
  const text = String(value ?? '').replace(/\r?\n/g, ' ').trim();
  return /[",;\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function safeJson(value) {
  return JSON.stringify(value || {}, null, 2);
}

function isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function isTimestampLike(value) {
  return isPlainObject(value) && (
    value.__type === 'timestamp' ||
    value._isFsTimestamp === true ||
    typeof value.seconds === 'number' ||
    typeof value._seconds === 'number'
  );
}

function timestampInputValue(value) {
  const ms = tsToMillis(value);
  return ms ? new Date(ms).toISOString().slice(0, 16) : '';
}

function valueTypeLabel(value) {
  if (isTimestampLike(value)) return 'fecha';
  if (Array.isArray(value)) return 'lista';
  if (value === null) return 'null';
  return typeof value;
}

function fieldGridSpan(value) {
  if (Array.isArray(value)) return 'md:col-span-2 xl:col-span-4';
  if (isPlainObject(value) && !isTimestampLike(value)) return 'md:col-span-2 xl:col-span-4';
  const str = typeof value === 'string' ? value : '';
  if (str.length > 80) return 'md:col-span-2';
  return '';
}

const TOP_LEVEL_FIELD_ORDER = [
  'plan',
  'ADMIN',
  'ADMIN_VIEWER',
  'email',
  'language',
  'appVersion',
  'appVersionLastSeenAt',
  'createdAt',
  'updatedAt',
  'ambassador',
  'founder'
];

function compareFirestoreFields(path, a, b) {
  if (path.length > 0) return a.localeCompare(b);
  const ia = TOP_LEVEL_FIELD_ORDER.indexOf(a);
  const ib = TOP_LEVEL_FIELD_ORDER.indexOf(b);
  const ra = ia === -1 ? TOP_LEVEL_FIELD_ORDER.length : ia;
  const rb = ib === -1 ? TOP_LEVEL_FIELD_ORDER.length : ib;
  return ra === rb ? a.localeCompare(b) : ra - rb;
}

const FIELD_DESCRIPTIONS = {
  ADMIN: 'Permite acceso completo al backoffice.',
  ADMIN_VIEWER: 'Permite ver el backoffice sin editar datos.',
  createdAt: 'Fecha en la que se creo el usuario.',
  updatedAt: 'Ultima actualizacion guardada en Firestore.',
  email: 'Correo principal asociado a la cuenta.',
  language: 'Idioma preferido del usuario en la app.',
  appVersion: 'Ultima version de TikControl detectada al iniciar la app.',
  appVersionLastSeenAt: 'Ultimo inicio en el que la app guardo su version.',
  plan: 'Plan actual asignado al usuario.',
  welcomeEmailSent: 'Indica si ya se envio el email de bienvenida.',
  stripeCancelAtPeriodEnd: 'Marca si la suscripcion termina al final del periodo.',
  stripeCanceledAt: 'Fecha en la que se solicito o confirmo la cancelacion.',
  stripeCustomerId: 'ID del cliente en Stripe.',
  stripeSubscriptionId: 'ID de la suscripcion actual en Stripe.',
  stripeStatus: 'Estado actual de la suscripcion segun Stripe.',
  stripeCurrentPeriodStart: 'Inicio del periodo de facturacion actual.',
  stripeCurrentPeriodEnd: 'Fin del periodo de facturacion actual.',
  stripePriceId: 'Precio de Stripe asociado al plan.',
  stripeTrialDaysGranted: 'Dias de prueba concedidos.',
  stripeTrialPlan: 'Plan que se uso durante la prueba.',
  stripeTrialSubscriptionId: 'Suscripcion que se creo para la prueba.',
  stripeTrialUsed: 'Indica si el usuario ya uso su prueba.',
  stripeTrialUsedAt: 'Fecha en la que se marco la prueba como usada.',
  lastWebhookUpdate: 'Ultima vez que Stripe actualizo este usuario.',
  lastPlanActivationEmailClaimedAt: 'Momento en el que se reservo el envio del email de activacion.',
  lastPlanActivationEmailPlan: 'Plan sobre el que se envio el email de activacion.',
  lastPlanActivationEmailSentAt: 'Momento en el que se envio el email de activacion.',
  lastPlanActivationEmailSessionId: 'Checkout Session asociada al email de activacion.',
  lastPlanActivationEmailSubscriptionId: 'Suscripcion asociada al email de activacion.',
  pendingPaymentStatus: 'Estado del aviso de pago pendiente.',
  pendingPaymentLink: 'Link enviado al usuario para resolver el pago.',
  pendingPaymentToken: 'Token interno del link de pago pendiente.',
  stripePaymentIssue: 'Motivo registrado para el problema de pago.',
  founderActive: 'Indica si el usuario conserva ventajas Founder.',
  founderNumber: 'Numero Founder asignado al usuario.',
  founderAssignedAt: 'Fecha en la que se asigno el Founder.',
  founderPurchasedAt: 'Fecha en la que se completo el pago Founder.',
  ambassadorNumber: 'Identificador visual Ambassador. Todos muestran #0000 junto al idioma.',
  ambassadorLanguage: 'Idioma del Ambassador, por ejemplo ES, EN o PT.',
  ambassadorRegion: 'Campo legacy de idioma Ambassador.',
  tiktokChangeCount: 'Numero de cambios de cuenta de TikTok vinculada.',
  tiktokLastChangeAt: 'Ultimo cambio de cuenta de TikTok vinculada.',
  tiktokLinkedUsername: 'Usuario de TikTok vinculado actualmente.',
  tiktokNumericId: 'ID numerico interno de TikTok.',
  tiktokUsername: 'Nombre visible de TikTok vinculado.',
  'founder.active': 'Indica si el Founder esta activo.',
  'founder.number': 'Numero Founder asignado.',
  'founder.status': 'Estado de negocio del Founder: trial, activo o cancelado.',
  'founder.paddedNumber': 'Numero Founder formateado para mostrarlo.',
  'founder.stripeSubscriptionId': 'Suscripcion Stripe asociada al Founder.',
  'founder.assignedAt': 'Fecha de asignacion del Founder.',
  'founder.purchasedAt': 'Fecha del pago real del Founder.',
  'ambassador.language': 'Idioma del Ambassador, por ejemplo ES, EN o PT.',
  'ambassador.region': 'Campo legacy de idioma Ambassador.',
  'ambassador.number': 'Campo legacy. La app muestra siempre #0000.',
  'ambassador.paddedNumber': 'Campo legacy. La app muestra siempre #0000.',
  'ambassador.active': 'Indica si el badge Ambassador esta activo.',
  'betaTermsAccepted.acceptedAt': 'Fecha de aceptacion de terminos beta.',
  'betaTermsAccepted.appVersion': 'Version de app usada al aceptar.',
  'betaTermsAccepted.ip': 'IP desde la que se aceptaron los terminos.',
  'betaTermsAccepted.lang': 'Idioma usado al aceptar los terminos.',
  'betaTermsAccepted.sessionOrDeviceId': 'Sesion o dispositivo que acepto los terminos.',
  'betaTermsAccepted.termsVersion': 'Version de terminos aceptada.'
};

function fieldDescription(path, label, type) {
  const exact = FIELD_DESCRIPTIONS[path];
  if (exact) return exact;
  const byLabel = FIELD_DESCRIPTIONS[label];
  if (byLabel) return byLabel;
  if (type === 'fecha') return 'Fecha guardada en Firestore.';
  if (type === 'boolean') return 'Valor true/false usado por la app.';
  if (type === 'number') return 'Valor numerico guardado en Firestore.';
  if (type === 'null') return 'Campo sin valor actualmente.';
  if (type === 'lista') return 'Lista de valores guardada como JSON.';
  return `Campo Firestore: ${path}`;
}

function FieldShell({ label, path, type, children }) {
  const description = fieldDescription(path, label, type);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold text-default-600" title={label}>{label}</span>
        <span className="shrink-0 rounded bg-default-100 px-1.5 py-0.5 text-[10px] uppercase text-default-400">{type}</span>
      </div>
      {children}
      <span className="line-clamp-2 min-h-[28px] text-[11px] leading-4 text-default-400" title={path}>{description}</span>
    </div>
  );
}

function setPathValue(source, path, value) {
  const root = JSON.parse(JSON.stringify(source || {}));
  let cursor = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (!isPlainObject(cursor[key]) && !Array.isArray(cursor[key])) cursor[key] = {};
    cursor = cursor[key];
  }
  cursor[path[path.length - 1]] = value;
  return root;
}

function deletePathValue(source, path) {
  const root = JSON.parse(JSON.stringify(source || {}));
  let cursor = root;
  for (let i = 0; i < path.length - 1; i++) {
    cursor = cursor?.[path[i]];
    if (!isPlainObject(cursor)) return root;
  }
  delete cursor[path[path.length - 1]];
  return root;
}

function getPathValue(source, path) {
  let cursor = source;
  for (const key of path) {
    if (!isPlainObject(cursor)) return undefined;
    cursor = cursor[key];
  }
  return cursor;
}

const NEW_FIELD_DEFAULTS = {
  string: () => '',
  number: () => 0,
  boolean: () => false,
  timestamp: () => ({ __type: 'timestamp', value: new Date().toISOString() }),
  map: () => ({})
};

export default function Users() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [planFilter, setPlanFilter] = useState([]);
  const [subscriptionFilter, setSubscriptionFilter] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(EMPTY_ADVANCED_FILTERS);
  const [advancedDraft, setAdvancedDraft] = useState(EMPTY_ADVANCED_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pageSize, setPageSize] = useState('50');
  const [nextPageToken, setNextPageToken] = useState(null);
  const [pageStack, setPageStack] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [scanned, setScanned] = useState(0);
  const [totalMatching, setTotalMatching] = useState(0);
  const [userFacets, setUserFacets] = useState(null);
  const [showUserFacets, setShowUserFacets] = useState(false);
  const [planDraft, setPlanDraft] = useState([]);
  const [subscriptionDraft, setSubscriptionDraft] = useState([]);
  const [sortDescriptor, setSortDescriptor] = useState({ column: 'createdAt', direction: 'descending' });
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(DEFAULT_VISIBLE_USER_COLUMNS));
  const [workerStatus, setWorkerStatus] = useState('checking');
  const [toast, setToast] = useState(null);
  const [liveUsers, setLiveUsers] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState(null);
  const [liveSortDescriptor, setLiveSortDescriptor] = useState({ column: 'lastUpdated', direction: 'descending' });
  const navigate = useNavigate();
  const { isViewer, canWrite } = useAuth();

  const jsonModal = useDisclosure();
  const deleteModal = useDisclosure();
  const paymentNoticesModal = useDisclosure();
  const historyModal = useDisclosure();
  const [historyUser, setHistoryUser] = useState(null);
  const [selected, setSelected] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('string');
  const [jsonError, setJsonError] = useState('');
  const [savingJson, setSavingJson] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [repairingPaymentNotices, setRepairingPaymentNotices] = useState(false);
  const [backfillingCanceledAt, setBackfillingCanceledAt] = useState(false);
  const [robloxLookupOpen, setRobloxLookupOpen] = useState(false);
  const [paymentNoticePreview, setPaymentNoticePreview] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    apiFetch('/api/backoffice/me')
      .then((res) => setWorkerStatus(res?.ok ? 'ok' : 'error'))
      .catch(() => setWorkerStatus('error'));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const refreshMs = activeTab === 'live' ? LIVE_REFRESH_ACTIVE_MS : LIVE_REFRESH_IDLE_MS;

    async function load() {
      try {
        const res = await apiFetch('/api/backoffice/live');
        if (cancelled) return;
        setLiveUsers(Array.isArray(res?.creators) ? res.creators : []);
        setLiveError(null);
      } catch (err) {
        if (cancelled) return;
        setLiveError(err.message || 'Error cargando usuarios en live');
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
      if (!cancelled) timer = setTimeout(load, refreshMs);
    }

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeTab]);

  function buildUsersPath(token, overrides = {}) {
    const params = new URLSearchParams({
      limit: overrides.limit || pageSize,
      plan: filterParam(planFilter),
      subscription: filterParam(subscriptionFilter),
      sort: sortDescriptor.column || 'createdAt',
      direction: sortDescriptor.direction || 'descending'
    });
    if (overrides.export) params.set('export', '1');
    if (appliedSearch.trim()) params.set('search', appliedSearch.trim());
    for (const key of ['createdFrom', 'createdTo', 'endFrom', 'endTo', 'cancelFrom', 'cancelTo', 'lastSeenFrom', 'lastSeenTo']) {
      if (advancedFilters[key]) params.set(key, advancedFilters[key]);
    }
    if (advancedFilters.appVersion.trim()) params.set('appVersion', advancedFilters.appVersion.trim());
    if (advancedFilters.language !== 'all') params.set('language', advancedFilters.language);
    if (advancedFilters.role !== 'all') params.set('role', advancedFilters.role);
    if (token) params.set('pageToken', token);
    return `/api/backoffice/users?${params.toString()}`;
  }

  async function loadUsers(token = null, nextPage = 1, stack = pageStack) {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(buildUsersPath(token));
      setUsers(Array.isArray(res.rows) ? res.rows : []);
      setNextPageToken(res.nextPageToken || null);
      setScanned(res.scanned || 0);
      setTotalMatching(res.totalMatching || 0);
      setUserFacets(res.facets || null);
      setPageIndex(nextPage);
      setPageStack(stack);
    } catch (e) {
      setError(e.message);
      setUsers([]);
      setNextPageToken(null);
      setTotalMatching(0);
      setUserFacets(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers(null, 1, []);
  }, [planFilter, subscriptionFilter, advancedFilters, pageSize, appliedSearch, sortDescriptor]);

  const activeLiveUsers = useMemo(
    () => liveUsers.filter((u) => u.isLive === true),
    [liveUsers]
  );

  const visibleUserColumns = useMemo(() => {
    return USER_COLUMNS.filter((column) => visibleColumns.has(column.key));
  }, [visibleColumns]);

  const sortedLiveUsers = useMemo(
    () => applySort(activeLiveUsers, liveSortDescriptor, {
      tiktok: (u) => normalizeSortText(u.username || u.tiktokUsername),
      viewerCount: (u) => Number(u.viewerCount || 0),
      lastUpdated: (u) => tsToMillis(u.lastUpdated),
      uid: (u) => normalizeSortText(u.userId || u.id)
    }),
    [activeLiveUsers, liveSortDescriptor]
  );

  const activeAdvancedCount = countActiveAdvancedFilters(advancedFilters)
    + (planFilter.length ? 1 : 0)
    + (subscriptionFilter.length ? 1 : 0);

  function applySearch() {
    setAppliedSearch(search.trim());
  }

  function clearFilters() {
    setSearch('');
    setAppliedSearch('');
    setPlanFilter([]);
    setSubscriptionFilter([]);
    setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
    setAdvancedDraft(EMPTY_ADVANCED_FILTERS);
    setPageSize('50');
  }

  function applyAdvancedFilters() {
    setAdvancedFilters({ ...advancedDraft, appVersion: advancedDraft.appVersion.trim() });
    setPlanFilter(planDraft);
    setSubscriptionFilter(subscriptionDraft);
    setFiltersOpen(false);
  }

  function clearAdvancedFilters() {
    setAdvancedDraft(EMPTY_ADVANCED_FILTERS);
    setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
    setPlanDraft([]);
    setSubscriptionDraft([]);
    setPlanFilter([]);
    setSubscriptionFilter([]);
    setFiltersOpen(false);
  }

  function setDraftField(key, value) {
    setAdvancedDraft((draft) => ({ ...draft, [key]: value }));
  }

  // Función render (no componente) a propósito: un componente definido dentro
  // de Users se remontaría en cada render y los inputs perderían el foco.
  function renderAdvancedDateRange(title, fromKey, toKey) {
    return (
      <div className="rounded-lg border border-default-200 p-2">
        <div className="mb-1 text-xs font-semibold text-default-600">{title}</div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            size="sm"
            type="date"
            label="Desde"
            value={advancedDraft[fromKey]}
            onValueChange={(v) => setDraftField(fromKey, v)}
          />
          <Input
            size="sm"
            type="date"
            label="Hasta"
            value={advancedDraft[toKey]}
            onValueChange={(v) => setDraftField(toKey, v)}
          />
        </div>
      </div>
    );
  }

  function nextPage() {
    if (!nextPageToken) return;
    loadUsers(nextPageToken, pageIndex + 1, [...pageStack, nextPageToken]);
  }

  function prevPage() {
    if (pageIndex <= 1) return;
    const nextStack = pageStack.slice(0, -1);
    const prevToken = nextStack[nextStack.length - 1] || null;
    loadUsers(prevToken, pageIndex - 1, nextStack);
  }

  async function handleResetPassword(user) {
    if (!user.email) {
      setToast({ type: 'error', msg: 'Este usuario no tiene email.' });
      return;
    }
    if (!confirm(`Enviar email de reset de contrasena a ${user.email}?`)) return;
    try {
      // En el idioma guardado del usuario, no en el de quien lo manda. Lo
      // manda TikControl (Worker); si falla, Firebase. Sin idioma guardado,
      // el Worker mira el de Stripe y, si no, inglés.
      const locale = globalThis.TCFirebaseLocale;
      const lang = String(user.language || '');
      const send = () => sendPasswordResetEmail(auth, user.email);
      const res = locale
        ? await locale.sendPasswordReset({ auth, email: user.email, lang, send })
        : (await send(), { via: 'firebase', lang: null });
      const sentIn = (locale && locale.localeFor(lang)) || (res.via === 'worker' ? 'el de su cuenta o inglés' : 'en');
      const by = res.via === 'worker' ? 'TikControl' : 'Firebase';
      setToast({ type: 'ok', msg: `Email de reset enviado a ${user.email} (por ${by}, idioma: ${sentIn})` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  async function openJson(user) {
    setSelected(user);
    setJsonData(null);
    setJsonError('');
    setNewFieldName('');
    setNewFieldType('string');
    jsonModal.onOpen();
    try {
      const uid = user.id || user.uid;
      const res = await apiFetch(`/api/backoffice/users/${encodeURIComponent(uid)}/profile`);
      setJsonData(res?.firestore?.data || {});
    } catch (e) {
      setJsonData(user || {});
      setJsonError(`No se pudo cargar el documento puro de Firestore: ${e.message}`);
    }
  }

  async function saveJson() {
    if (!selected) return;
    setSavingJson(true);
    setJsonError('');
    try {
      const uid = selected.id || selected.uid;
      const res = await apiFetch(`/api/backoffice/users/${encodeURIComponent(uid)}/firestore`, {
        method: 'PUT',
        body: { data: jsonData || {} }
      });
      const nextUser = { id: uid, uid, ...(res.data || jsonData || {}) };
      setUsers((list) => list.map((u) => (u.id === uid ? nextUser : u)));
      setToast({ type: 'ok', msg: 'Firestore actualizado.' });
      jsonModal.onClose();
    } catch (e) {
      setJsonError(e.message);
    } finally {
      setSavingJson(false);
    }
  }

  function openDelete(user) {
    setSelected(user);
    deleteModal.onOpen();
  }

  // Historial de cambios de ESE usuario (quién le dio o quitó la licencia…).
  function openHistory(user) {
    setHistoryUser(user);
    historyModal.onOpen();
  }

  async function deleteUser() {
    if (!selected) return;
    const uid = selected.id || selected.uid;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/backoffice/users/${encodeURIComponent(uid)}`, {
        method: 'DELETE'
      });
      setUsers((list) => list.filter((u) => (u.id || u.uid) !== uid));
      const done = res.done || {};
      const n = (table) => Number(done.d1?.[table] || 0);
      const errors = Array.isArray(done.errors) ? done.errors.length : 0;
      setToast({
        type: res.ok === false ? 'error' : 'ok',
        msg: `Usuario eliminado. Firestore: ${res.firestoreDeleted ? 'si' : 'no'} / Auth: ${res.authDeleted ? 'si' : 'no'} / perfiles nube: ${n('cloud_profiles')} / configs: ${n('configs')} / archivos: ${Number(done.r2Deleted || 0)}${errors ? ` / errores: ${errors}` : ''}`
      });
      deleteModal.onClose();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setDeleting(false);
    }
  }

  async function previewPaymentNotices() {
    setRepairingPaymentNotices(true);
    try {
      const preview = await apiFetch('/api/backoffice/subscriptions/repair-payment-notices', {
        method: 'POST',
        body: { dryRun: true, limit: 1000 }
      });
      if (!preview.candidates) {
        setToast({ type: 'ok', msg: 'No hay usuarios past_due/unpaid pendientes de aviso.' });
        return;
      }
      setPaymentNoticePreview(preview);
      paymentNoticesModal.onOpen();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setRepairingPaymentNotices(false);
    }
  }

  async function sendPaymentNotices() {
    setRepairingPaymentNotices(true);
    try {
      const res = await apiFetch('/api/backoffice/subscriptions/repair-payment-notices', {
        method: 'POST',
        body: { dryRun: false, limit: 1000 }
      });
      setToast({
        type: res.failed ? 'error' : 'ok',
        msg: `Avisos procesados. Enviados: ${res.sent}, actualizados: ${res.updated}, omitidos: ${res.skipped}, fallidos: ${res.failed}.`
      });
      setPaymentNoticePreview(res);
      paymentNoticesModal.onClose();
      loadUsers(null, 1, []);
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setRepairingPaymentNotices(false);
    }
  }

  async function backfillCanceledAt() {
    const ok = confirm('Esto consultara Stripe por lotes pequenos y guardara stripeCanceledAt en Firestore. ¿Continuar?');
    if (!ok) return;

    setBackfillingCanceledAt(true);
    try {
      const totals = {
        scanned: 0,
        candidates: 0,
        checked: 0,
        found: 0,
        updated: 0,
        skipped: 0,
        failed: 0
      };
      let pageToken = null;
      let pages = 0;

      do {
        const res = await apiFetch('/api/backoffice/subscriptions/backfill-canceled-at', {
          method: 'POST',
          body: { dryRun: false, pageSize: 20, pageToken }
        });
        pages++;
        for (const key of Object.keys(totals)) {
          totals[key] += Number(res[key] || 0);
        }
        pageToken = res.nextPageToken || null;
        setToast({
          type: res.failed ? 'error' : 'ok',
          msg: `Backfill en curso: ${totals.updated} guardadas, ${totals.checked} revisadas en ${pages} lote(s).`
        });
      } while (pageToken);

      setToast({
        type: totals.failed ? 'error' : 'ok',
        msg: `Backfill terminado. Guardadas: ${totals.updated}. Encontradas: ${totals.found}, revisadas: ${totals.checked}, omitidas: ${totals.skipped}, fallidas: ${totals.failed}.`
      });
      loadUsers(null, 1, []);
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setBackfillingCanceledAt(false);
    }
  }

  function updateJsonField(path, value) {
    setJsonData((current) => setPathValue(current || {}, path, value));
  }

  function deleteJsonMap(path) {
    setJsonData((current) => deletePathValue(current || {}, path));
    setJsonError('');
  }

  function ensureJsonMap(field, defaults) {
    setJsonData((current) => {
      const next = JSON.parse(JSON.stringify(current || {}));
      if (!isPlainObject(next[field])) next[field] = defaults;
      return next;
    });
    setJsonError('');
  }

  function addJsonField() {
    const name = newFieldName.trim();
    if (!name) return;
    // Permite anidar con puntos: "mapa.subcampo" crea el map intermedio.
    const path = name.split('.').map((part) => part.trim());
    if (path.some((part) => !part)) {
      setJsonError('Nombre de campo invalido: revisa los puntos.');
      return;
    }
    if (getPathValue(jsonData || {}, path) !== undefined) {
      setJsonError(`El campo "${name}" ya existe.`);
      return;
    }
    const makeDefault = NEW_FIELD_DEFAULTS[newFieldType] || NEW_FIELD_DEFAULTS.string;
    updateJsonField(path, makeDefault());
    setNewFieldName('');
    setJsonError('');
  }

  function updateComplexJsonField(path, raw) {
    try {
      const parsed = JSON.parse(raw);
      updateJsonField(path, parsed);
      setJsonError('');
    } catch (e) {
      setJsonError(`JSON invalido en ${path.join('.')}: ${e.message}`);
    }
  }

  function renderFieldEditor(path, value) {
    const key = path.join('.');
    const label = path[path.length - 1];
    const type = valueTypeLabel(value);
    const inputClass = 'h-10 w-full rounded-md border border-default-200 bg-default-100 px-3 text-sm text-foreground outline-none transition focus:border-primary';

    if (isTimestampLike(value)) {
      return (
        <FieldShell label={label} path={key} type={type}>
          <input
            className={inputClass}
          type="datetime-local"
            value={timestampInputValue(value)}
            onChange={(e) => updateJsonField(path, e.target.value ? { __type: 'timestamp', value: new Date(e.target.value).toISOString() } : null)}
          />
        </FieldShell>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <FieldShell label={label} path={key} type={type}>
          <select
            className={inputClass}
            value={String(value)}
            onChange={(e) => updateJsonField(path, e.target.value === 'true')}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </FieldShell>
      );
    }

    if (typeof value === 'number') {
      return (
        <FieldShell label={label} path={key} type={type}>
          <input
            className={inputClass}
          type="number"
            value={String(value)}
            onChange={(e) => updateJsonField(path, e.target.value === '' ? null : Number(e.target.value))}
          />
        </FieldShell>
      );
    }

    if (typeof value === 'string' || value == null) {
      if (label === 'plan') {
        const currentPlan = value == null ? 'free' : String(value);
        const options = USER_PLAN_OPTIONS.includes(currentPlan)
          ? USER_PLAN_OPTIONS
          : [currentPlan, ...USER_PLAN_OPTIONS];
        return (
          <FieldShell label={label} path={key} type="plan">
            <select
              className={inputClass}
              value={currentPlan}
              onChange={(e) => updateJsonField(path, e.target.value)}
            >
              {options.map((plan) => (
                <option key={plan} value={plan}>{formatPlan(plan)}</option>
              ))}
            </select>
          </FieldShell>
        );
      }

      if (key === 'ambassador.language' || key === 'ambassador.region' || label === 'ambassadorLanguage' || label === 'ambassadorRegion') {
        const currentLanguage = String(value || 'ES').toUpperCase();
        const options = AMBASSADOR_LANGUAGE_OPTIONS.includes(currentLanguage)
          ? AMBASSADOR_LANGUAGE_OPTIONS
          : [currentLanguage, ...AMBASSADOR_LANGUAGE_OPTIONS];
        return (
          <FieldShell label={label} path={key} type="idioma">
            <select
              className={inputClass}
              value={currentLanguage}
              onChange={(e) => updateJsonField(path, e.target.value)}
            >
              {options.map((language) => (
                <option key={language} value={language}>{language}</option>
              ))}
            </select>
          </FieldShell>
        );
      }

      return (
        <FieldShell label={label} path={key} type={type}>
          <input
            className={inputClass}
            value={value == null ? '' : String(value)}
          placeholder={value == null ? 'null' : undefined}
            onChange={(e) => updateJsonField(path, e.target.value)}
          />
        </FieldShell>
      );
    }

    return (
      <FieldShell label={label} path={key} type={type}>
        <textarea
          defaultValue={safeJson(value)}
          onChange={(e) => updateComplexJsonField(path, e.target.value)}
          spellCheck={false}
          className="min-h-[110px] resize-y rounded-lg border border-default-200 bg-default-100 p-3 font-mono text-xs leading-5 outline-none transition focus:border-primary"
        />
      </FieldShell>
    );
  }

  function renderFirestoreFields(value, path = []) {
    if (!isPlainObject(value) || isTimestampLike(value)) {
      return renderFieldEditor(path.length ? path : ['value'], value);
    }

    return Object.entries(value)
      .sort(([a], [b]) => compareFirestoreFields(path, a, b))
      .map(([field, fieldValue]) => {
        const nextPath = [...path, field];
        const fieldKey = nextPath.join('.');
        if (isPlainObject(fieldValue) && !isTimestampLike(fieldValue)) {
          const canDeleteMap = fieldKey === 'founder' || fieldKey === 'ambassador';
          return (
            <details key={fieldKey} className="rounded-lg border border-default-200 bg-content1 md:col-span-2 xl:col-span-4" open={path.length === 0}>
              <summary className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm font-semibold">
                <span>
                  {field} <span className="text-xs font-normal text-default-400">map</span>
                </span>
                {canDeleteMap && (
                  <button
                    type="button"
                    className="rounded-md border border-danger-200 bg-danger-50 px-2 py-1 text-xs font-medium text-danger-600 transition hover:border-danger-300 hover:bg-danger-100"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteJsonMap(nextPath);
                    }}
                  >
                    Eliminar map
                  </button>
                )}
              </summary>
              <div className="grid grid-cols-1 gap-3 border-t border-default-100 p-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {renderFirestoreFields(fieldValue, nextPath)}
              </div>
            </details>
          );
        }
        return (
          <div key={fieldKey} className={`rounded-lg border border-default-200 bg-content1 p-3 ${fieldGridSpan(fieldValue)}`}>
            {renderFieldEditor(nextPath, fieldValue)}
          </div>
        );
      });
  }

  function userCellText(user, columnKey) {
    const uid = user.id || user.uid || '';
    const email = isViewer ? maskEmail(user.email) : user.email || '';
    const tiktok = user.tiktokUsername
      ? (isViewer ? `@${maskHandle(user.tiktokUsername)}` : `@${user.tiktokUsername}`)
      : '';
    const values = {
      email: uid ? `${email || '-'}\n${isViewer ? '********' : uid}` : email || '-',
      tiktok: tiktok || '-',
      plan: formatPlan(user.plan),
      subscription: subscriptionText(user),
      subscriptionDate: formatDateTime(subscriptionDate(user)),
      cancellationDate: formatDateTime(cancellationDate(user)),
      stripeCustomerId: isViewer ? '********' : stripeCustomerIdOf(user) || '-',
      stripeSubscriptionId: isViewer ? '********' : stripeSubscriptionIdOf(user) || '-',
      appVersion: formatAppVersion(user.appVersion),
      appVersionLastSeenAt: formatDateTime(user.appVersionLastSeenAt),
      language: normalizeLanguage(user.language),
      createdAt: formatDateTime(user.createdAt),
      planEnd: formatDateTime(effectivePlanEnd(user)),
      founderNumber: founderRawValue(user) ? `#${founderRawValue(user)}` : '-',
      ambassadorNumber: ambassadorLabel(user) || '-',
      admin: user.ADMIN === true ? 'ADMIN' : user.ADMIN_VIEWER === true ? 'VIEWER' : '-'
    };
    return values[columnKey] ?? '-';
  }

  async function fetchAllUsersForExport() {
    const allRows = [];
    let token = null;
    let pages = 0;

    do {
      const res = await apiFetch(buildUsersPath(token, { limit: EXPORT_PAGE_SIZE, export: true }));
      const rows = Array.isArray(res.rows) ? res.rows : [];
      allRows.push(...rows);
      token = res.nextPageToken || null;
      pages++;
      if (res.truncated && !token) {
        throw new Error(`Exportacion incompleta: el Worker reviso el limite de ${Number(res.scanLimit || 0).toLocaleString()} documentos.`);
      }
      if (token) {
        setToast({
          type: 'ok',
          msg: `Exportando usuarios: ${allRows.length.toLocaleString()} cargados...`
        });
      }
      if (pages > 100) throw new Error('Exportacion detenida: demasiadas paginas de usuarios.');
    } while (token);

    return allRows;
  }

  function buildUsersCsv(usersToExport, columns) {
    const header = columns.map((column) => escapeCsv(column.label)).join(',');
    const rows = usersToExport.map((user) => (
      columns.map((column) => escapeCsv(userCellText(user, column.key))).join(',')
    ));
    return ['\ufeff' + header, ...rows].join('\r\n');
  }

  async function exportUsersFile(format) {
    setExporting(format);
    try {
      const rowsToExport = await fetchAllUsersForExport();
      const columns = USER_COLUMNS.filter((column) => visibleColumns.has(column.key));
      const today = new Date().toISOString().slice(0, 10);

      if (format === 'csv') {
        const csv = buildUsersCsv(rowsToExport, columns);
        downloadBlob(`tikcontrol-users-${today}.csv`, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      } else {
        const header = columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('');
        const bodyRows = rowsToExport.map((user) => (
          `<tr>${columns.map((column) => `<td style="mso-number-format:'\\@';">${escapeHtml(userCellText(user, column.key))}</td>`).join('')}</tr>`
        )).join('');
        const html = `<!doctype html><html><head><meta charset="utf-8" /></head><body><table>${header ? `<thead><tr>${header}</tr></thead>` : ''}<tbody>${bodyRows}</tbody></table></body></html>`;
        downloadBlob(`tikcontrol-users-${today}.xls`, new Blob(['\ufeff', html], {
          type: 'application/vnd.ms-excel;charset=utf-8'
        }));
      }

      setToast({ type: 'ok', msg: `${rowsToExport.length.toLocaleString()} usuarios exportados.` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setExporting(null);
    }
  }

  async function exportUsersForResend() {
    setExporting('resend');
    try {
      const rowsToExport = await fetchAllUsersForExport();
      const resendColumns = [
        { label: 'Email', value: (user) => user.email || '' },
        { label: 'Idioma', value: (user) => normalizeLanguage(user.language) },
        { label: 'Tiktok username', value: (user) => user.tiktokUsername || '' }
      ];
      const buildResendCsv = (list) => [
        '\ufeff' + resendColumns.map((column) => escapeCsv(column.label)).join(','),
        ...list.map((user) => resendColumns.map((column) => escapeCsv(column.value(user))).join(','))
      ].join('\r\n');
      const spanishUsers = rowsToExport.filter((user) => normalizeLanguage(user.language) === 'ES');
      const otherUsers = rowsToExport.filter((user) => normalizeLanguage(user.language) !== 'ES');
      const today = new Date().toISOString().slice(0, 10);

      downloadBlob(`tikcontrol-resend-es-${today}.csv`, new Blob([buildResendCsv(spanishUsers)], { type: 'text/csv;charset=utf-8' }));
      downloadBlob(`tikcontrol-resend-resto-${today}.csv`, new Blob([buildResendCsv(otherUsers)], { type: 'text/csv;charset=utf-8' }));
      setToast({
        type: 'ok',
        msg: `CSV de Resend generados: ${spanishUsers.length.toLocaleString()} ES y ${otherUsers.length.toLocaleString()} resto.`
      });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setExporting(null);
    }
  }

  function exportUsersExcel() {
    exportUsersFile('excel');
  }

  function exportUsersCsv() {
    exportUsersFile('csv');
  }

  // Botones de acción de la fila: van debajo del uid, en la celda de email,
  // en vez de en una columna propia al final de la tabla.
  function renderUserActions(user) {
    const uid = user.id || user.uid;
    const action = 'h-6 min-w-0 px-2 text-[11px]';
    if (!canWrite) {
      return (
        <div className="mt-1 flex flex-wrap gap-1">
          <Button size="sm" variant="flat" className={action} onPress={() => navigate(`/users/${encodeURIComponent(uid)}`)}>
            Perfil
          </Button>
          <Button size="sm" variant="light" className={action} onPress={() => openHistory(user)}>
            Historial
          </Button>
        </div>
      );
    }
    const stripeUrl = stripeDashboardUrl(user);
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        <Button size="sm" variant="flat" className={action} onPress={() => navigate(`/users/${encodeURIComponent(uid)}`)}>
          Perfil
        </Button>
        {stripeUrl && (
          <Button as="a" href={stripeUrl} target="_blank" rel="noreferrer" size="sm" variant="flat" color="secondary" className={action}>
            Stripe
          </Button>
        )}
        <Button size="sm" variant="light" className={action} onPress={() => openJson(user)}>
          Editar
        </Button>
        <Button size="sm" variant="light" className={action} onPress={() => openHistory(user)}>
          Historial
        </Button>
        <Button size="sm" variant="flat" color="warning" className={action} onPress={() => handleResetPassword(user)} isDisabled={!user.email}>
          Reset
        </Button>
        <Button size="sm" variant="flat" color="danger" className={action} onPress={() => openDelete(user)}>
          Eliminar
        </Button>
      </div>
    );
  }

  function renderUserCell(user, columnKey) {
    const uid = user.id || user.uid;
    switch (columnKey) {
      case 'email':
        return (
          <div className="flex min-w-[220px] flex-col">
            <span>{isViewer ? maskEmail(user.email) : user.email || '-'}</span>
            <code className="break-all text-xs text-default-400">{isViewer ? '********' : uid}</code>
            {renderUserActions(user)}
          </div>
        );
      case 'tiktok':
        return user.tiktokUsername ? (
          isViewer ? (
            <span>@{maskHandle(user.tiktokUsername)}</span>
          ) : (
            <a
              href={`https://www.tiktok.com/@${user.tiktokUsername}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              @{user.tiktokUsername}
            </a>
          )
        ) : (
          <span className="text-default-400">-</span>
        );
      case 'plan':
        return <Chip size="sm" variant="flat">{formatPlan(user.plan)}</Chip>;
      case 'subscription':
        return subscriptionChip(user);
      case 'subscriptionDate':
        return formatDateTime(subscriptionDate(user));
      case 'cancellationDate':
        return formatDateTime(cancellationDate(user));
      case 'stripeCustomerId': {
        const customerId = stripeCustomerIdOf(user);
        if (!customerId) return <span className="text-default-400">-</span>;
        if (isViewer) return <code className="text-xs text-default-500">********</code>;
        return (
          <a
            href={`https://dashboard.stripe.com/customers/${encodeURIComponent(customerId)}`}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            <code className="text-xs">{customerId}</code>
          </a>
        );
      }
      case 'stripeSubscriptionId': {
        const subscriptionId = stripeSubscriptionIdOf(user);
        if (!subscriptionId) return <span className="text-default-400">-</span>;
        if (isViewer) return <code className="text-xs text-default-500">********</code>;
        return (
          <a
            href={`https://dashboard.stripe.com/subscriptions/${encodeURIComponent(subscriptionId)}`}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            <code className="text-xs">{subscriptionId}</code>
          </a>
        );
      }
      case 'appVersion':
        return user.appVersion ? (
          <Chip size="sm" color="primary" variant="flat">{formatAppVersion(user.appVersion)}</Chip>
        ) : (
          <span className="text-default-400">-</span>
        );
      case 'appVersionLastSeenAt':
        return formatDateTime(user.appVersionLastSeenAt);
      case 'language':
        return <Chip size="sm" variant="flat">{normalizeLanguage(user.language)}</Chip>;
      case 'createdAt':
        return formatDateTime(user.createdAt);
      case 'planEnd':
        return formatDateTime(effectivePlanEnd(user));
      case 'founderNumber':
        return founderRawValue(user) ? (
          <Chip size="sm" color="secondary" variant="flat">#{founderRawValue(user)}</Chip>
        ) : (
          <span className="text-default-400">-</span>
        );
      case 'ambassadorNumber':
        return ambassadorLabel(user) ? (
          <Chip size="sm" color="primary" variant="flat">{ambassadorLabel(user)}</Chip>
        ) : (
          <span className="text-default-400">-</span>
        );
      case 'admin':
        return roleChip(user);
      default:
        return '-';
    }
  }

  function renderLiveCell(user, columnKey) {
    switch (columnKey) {
      case 'tiktok':
        return user.username || user.tiktokUsername ? (
          isViewer ? (
            <span>@{maskHandle(user.username || user.tiktokUsername)}</span>
          ) : (
            <a
              href={`https://www.tiktok.com/@${user.username || user.tiktokUsername}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              @{user.username || user.tiktokUsername}
            </a>
          )
        ) : (
          '-'
        );
      case 'viewerCount':
        return <Chip size="sm" color="success" variant="flat">{Number(user.viewerCount || 0).toLocaleString()}</Chip>;
      case 'lastUpdated':
        return formatDateTime(user.lastUpdated);
      case 'uid':
        return <code className="text-xs text-default-500">{isViewer ? '********' : (user.userId || user.id)}</code>;
      default:
        return '-';
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Usuarios</h1>
          <div className="text-sm text-default-500">
            {loading ? 'Cargando...' : `${users.length} usuarios en esta pagina de ${Number(totalMatching || 0).toLocaleString()} encontrados`}
            {scanned > users.length && ` · ${scanned} documentos revisados por filtros`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <>
              <Button
                size="sm"
                variant="flat"
                color="secondary"
                onPress={backfillCanceledAt}
                isLoading={backfillingCanceledAt}
              >
                Backfill cancelaciones
              </Button>
              <Button size="sm" variant="flat" onPress={() => setRobloxLookupOpen(true)}>
                ID de Roblox
              </Button>
              <RobloxIdLookup isOpen={robloxLookupOpen} onClose={() => setRobloxLookupOpen(false)} />
              <Button
                size="sm"
                variant="flat"
                color="warning"
                onPress={previewPaymentNotices}
                isLoading={repairingPaymentNotices}
              >
                Enviar avisos impago
              </Button>
            </>
          )}
          <Chip
            size="sm"
            color={workerStatus === 'ok' ? 'success' : workerStatus === 'error' ? 'danger' : 'default'}
            variant="flat"
          >
            worker: {workerStatus}
          </Chip>
        </div>
      </div>

      <Tabs
        size="sm"
        selectedKey={activeTab}
        onSelectionChange={(k) => setActiveTab(String(k))}
        aria-label="Secciones de usuarios"
        className="max-w-full overflow-x-auto"
      >
        <Tab key="users" title="Usuarios" />
        <Tab key="live" title={`Live (${activeLiveUsers.length})`} />
        <Tab key="audit" title="Historial" />
      </Tabs>

      {toast && (
        <div
          className={`rounded-md border p-3 text-sm ${
            toast.type === 'ok'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-danger-200 bg-danger-50 text-danger-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>cerrar</button>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(260px,1fr)_120px_200px_auto]">
            <Input
              size="sm"
              label="Buscar"
              placeholder="Email, TikTok, UID, Stripe ID..."
              value={search}
              onValueChange={setSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applySearch();
              }}
              isClearable
            />
            <Select
              size="sm"
              label="Por pagina"
              selectedKeys={[pageSize]}
              onSelectionChange={(keys) => setPageSize(String(Array.from(keys)[0] || '50'))}
            >
              {PAGE_SIZE_OPTIONS.map((key) => <SelectItem key={key}>{key}</SelectItem>)}
            </Select>
            <Select
              size="sm"
              label="Columnas"
              selectionMode="multiple"
              selectedKeys={visibleColumns}
              onSelectionChange={(keys) => {
                const next = keys === 'all'
                  ? new Set(DEFAULT_VISIBLE_USER_COLUMNS)
                  : new Set(Array.from(keys));
                if (next.size > 0) setVisibleColumns(next);
              }}
            >
              {USER_COLUMNS.map((column) => (
                <SelectItem key={column.key}>{column.label}</SelectItem>
              ))}
            </Select>
            <div className="flex flex-wrap items-end gap-2">
              <Button size="sm" color="primary" onPress={applySearch} className="h-12 flex-1 md:flex-none">
                Buscar
              </Button>
              <Popover
                isOpen={filtersOpen}
                onOpenChange={(open) => {
                  setFiltersOpen(open);
                  if (open) {
                    setAdvancedDraft(advancedFilters);
                    setPlanDraft(planFilter);
                    setSubscriptionDraft(subscriptionFilter);
                  }
                }}
                placement="bottom-end"
              >
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="flat"
                    color={activeAdvancedCount > 0 ? 'secondary' : 'default'}
                    className="h-12 flex-1 md:flex-none"
                  >
                    Filtros{activeAdvancedCount > 0 ? ` (${activeAdvancedCount})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="max-h-[80vh] w-[560px] max-w-[calc(100vw-2rem)] overflow-auto p-4">
                  <div className="flex w-full flex-col gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold">Filtros</span>
                      <span className="text-xs text-default-400">Se suman a la busqueda</span>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {/* Casillas nativas: se pueden combinar varios planes/estados (OR). */}
                      <div className="rounded-lg border border-default-200 p-2">
                        <div className="mb-1 text-xs font-semibold text-default-600">Plan</div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {PLAN_FILTERS.filter(([key]) => key !== 'all').map(([key, label]) => (
                            <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-primary"
                                checked={planDraft.includes(key)}
                                onChange={() => setPlanDraft((list) => toggleFilterValue(list, key))}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-lg border border-default-200 p-2">
                        <div className="mb-1 text-xs font-semibold text-default-600">Suscripcion</div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {SUBSCRIPTION_FILTERS.filter(([key]) => key !== 'all').map(([key, label]) => (
                            <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-primary"
                                checked={subscriptionDraft.includes(key)}
                                onChange={() => setSubscriptionDraft((list) => toggleFilterValue(list, key))}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                      {renderAdvancedDateRange('Fecha de registro', 'createdFrom', 'createdTo')}
                      {renderAdvancedDateRange('Fecha de fin', 'endFrom', 'endTo')}
                      {renderAdvancedDateRange('Fecha de cancelacion', 'cancelFrom', 'cancelTo')}
                      {renderAdvancedDateRange('Ultimo inicio de la app', 'lastSeenFrom', 'lastSeenTo')}
                      <div className="rounded-lg border border-default-200 p-2">
                        <div className="mb-1 text-xs font-semibold text-default-600">Version de la app</div>
                        <Input
                          size="sm"
                          placeholder="p. ej. 1.0.35 (vale prefijo)"
                          value={advancedDraft.appVersion}
                          onValueChange={(v) => setDraftField('appVersion', v)}
                          isClearable
                        />
                      </div>
                      <div className="rounded-lg border border-default-200 p-2">
                        <div className="mb-1 text-xs font-semibold text-default-600">Idioma</div>
                        {/* Selects nativos: un Select de NextUI abriría un popover
                            anidado y cerraría el de filtros al elegir opción. */}
                        <select
                          className="h-10 w-full rounded-md border border-default-200 bg-default-100 px-3 text-sm text-foreground outline-none transition focus:border-primary"
                          aria-label="Idioma"
                          value={advancedDraft.language}
                          onChange={(e) => setDraftField('language', e.target.value)}
                        >
                          {LANGUAGE_FILTER_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                      </div>
                      <div className="rounded-lg border border-default-200 p-2 sm:col-span-2">
                        <div className="mb-1 text-xs font-semibold text-default-600">Rol de backoffice (Admin / Viewer)</div>
                        <select
                          className="h-10 w-full rounded-md border border-default-200 bg-default-100 px-3 text-sm text-foreground outline-none transition focus:border-primary"
                          aria-label="Rol de backoffice"
                          value={advancedDraft.role}
                          onChange={(e) => setDraftField('role', e.target.value)}
                        >
                          {ROLE_FILTER_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="flat" onPress={clearAdvancedFilters}>
                        Quitar filtros
                      </Button>
                      <Button size="sm" color="primary" onPress={applyAdvancedFilters}>
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button size="sm" variant="flat" onPress={clearFilters} className="h-12 flex-1 md:flex-none">
                Limpiar
              </Button>
              <Dropdown>
                <DropdownTrigger>
                  <Button size="sm" variant="flat" color="primary" isDisabled={loading || exporting || !users.length} isLoading={Boolean(exporting)} className="h-12 flex-1 md:flex-none">
                    Descargar
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Opciones de descarga"
                  onAction={(key) => {
                    if (key === 'excel') exportUsersExcel();
                    if (key === 'csv') exportUsersCsv();
                    if (key === 'resend') exportUsersForResend();
                  }}
                >
                  <DropdownItem key="excel">Excel completo</DropdownItem>
                  <DropdownItem key="csv">CSV completo</DropdownItem>
                  {!isViewer && (
                    <DropdownItem key="resend">
                      CSV para Resend
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          {userFacets && (
            <section className="border-t border-default-200 pt-4">
              {/* Movil: desplegable cerrado por defecto. Escritorio: siempre visible. */}
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left md:hidden"
                onClick={() => setShowUserFacets((value) => !value)}
                aria-expanded={showUserFacets}
              >
                <span className="text-sm font-semibold">Metricas de usuarios</span>
                <span className="text-xs text-primary">{showUserFacets ? 'Ocultar' : 'Mostrar'}</span>
              </button>
              <div className={`${showUserFacets ? 'mt-3 flex' : 'hidden'} flex-col gap-4 md:mt-0 md:flex xl:flex-row xl:items-start xl:divide-x xl:divide-default-200`}>
                <FacetGroup
                  title="Por plan"
                  subtitle={`${Number(userFacets.total || 0).toLocaleString()} usuarios`}
                  showReset={planFilter.length > 0}
                  onReset={() => setPlanFilter([])}
                >
                  {PLAN_COUNT_KEYS.map((key) => (
                    <FacetStat
                      key={key}
                      label={PLAN_LABELS[key]}
                      count={userFacets.planCounts?.[key]}
                      meta={PLAN_COUNT_META[key]}
                      active={planFilter.includes(key)}
                      onClick={() => setPlanFilter((list) => toggleFilterValue(list, key))}
                      info={planCountDetail(key, userFacets)}
                    />
                  ))}
                </FacetGroup>
                <FacetGroup
                  title="Por estado"
                  subtitle="suscripcion actual"
                  showReset={subscriptionFilter.length > 0}
                  onReset={() => setSubscriptionFilter([])}
                >
                  {SUBSCRIPTION_COUNT_KEYS.map((key) => {
                    const detail = subscriptionCountDetail(key, userFacets);
                    return (
                      <FacetStat
                        key={key}
                        label={SUBSCRIPTION_LABELS[key]}
                        count={userFacets.subscriptionCounts?.[key]}
                        meta={SUBSCRIPTION_COUNT_META[key]}
                        active={subscriptionFilter.includes(key)}
                        onClick={() => setSubscriptionFilter((list) => toggleFilterValue(list, key))}
                        info={detail ? detail.line : null}
                        infoContent={detail ? (
                          <ActiveBreakdownDetail
                            breakdown={detail.breakdown}
                            total={userFacets.subscriptionCounts?.[key] || 0}
                          />
                        ) : null}
                      />
                    );
                  })}
                </FacetGroup>
              </div>
            </section>
          )}

          {error && (
            <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center p-8"><Spinner /></div>
          ) : (
            <>
              <div className="overflow-x-auto border-t border-default-200">
                <Table
                  aria-label="Tabla de usuarios"
                  removeWrapper
                  sortDescriptor={sortDescriptor}
                  onSortChange={setSortDescriptor}
                  classNames={FLAT_TABLE_CLASSNAMES}
                >
                  <TableHeader columns={visibleUserColumns}>
                    {(column) => (
                      <TableColumn
                        key={column.key}
                        align={column.align || 'start'}
                        allowsSorting={column.sortable !== false}
                      >
                        {column.label}
                      </TableColumn>
                    )}
                  </TableHeader>
                  <TableBody items={users} emptyContent="No hay usuarios para estos filtros.">
                    {(user) => (
                      <TableRow key={user.id || user.uid}>
                        {(columnKey) => <TableCell>{renderUserCell(user, columnKey)}</TableCell>}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-default-500">Pagina {pageIndex}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="flat" onPress={prevPage} isDisabled={pageIndex <= 1 || loading}>
                    Anterior
                  </Button>
                  <Button size="sm" color="primary" variant="flat" onPress={nextPage} isDisabled={!nextPageToken || loading}>
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'live' && (
        <>
          {liveError && (
            <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              {liveError}
            </div>
          )}
          {liveLoading ? (
            <div className="flex justify-center p-8"><Spinner label="Cargando usuarios en live..." /></div>
          ) : (
            <div className="overflow-x-auto border-t border-default-200">
              <Table
                aria-label="Usuarios en live"
                removeWrapper
                sortDescriptor={liveSortDescriptor}
                onSortChange={setLiveSortDescriptor}
                classNames={FLAT_TABLE_CLASSNAMES}
              >
                <TableHeader columns={LIVE_COLUMNS}>
                  {(column) => (
                    <TableColumn key={column.key} allowsSorting>
                      {column.label}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={sortedLiveUsers} emptyContent="No hay usuarios en live.">
                  {(u) => (
                    <TableRow key={u.id}>
                      {(columnKey) => <TableCell>{renderLiveCell(u, columnKey)}</TableCell>}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {activeTab === 'audit' && (
        <>
          <p className="text-sm text-default-500">
            Cambios hechos desde el backoffice sobre los usuarios: quién dio o quitó una licencia, cambió un rol, editó la cuenta o la eliminó.
          </p>
          <AuditLog />
        </>
      )}

      <Modal
        isOpen={historyModal.isOpen}
        onClose={historyModal.onClose}
        size="4xl"
        scrollBehavior="inside"
        classNames={{ backdrop: 'bg-black/70' }}
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex flex-col">
              <span>Historial de cambios</span>
              <span className="text-xs font-normal text-default-400">
                {isViewer ? maskEmail(historyUser?.email) : historyUser?.email || historyUser?.id || historyUser?.uid}
              </span>
            </div>
          </ModalHeader>
          <ModalBody className="pb-6">
            {historyModal.isOpen && historyUser && (
              <AuditLog uid={historyUser.id || historyUser.uid} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={jsonModal.isOpen}
        onClose={jsonModal.onClose}
        size="5xl"
        scrollBehavior="inside"
        classNames={{
          wrapper: 'items-center p-4 sm:p-8 lg:p-10',
          backdrop: 'bg-black/70'
        }}
      >
        <ModalContent className="h-[82vh] max-h-[82vh] w-[min(1480px,calc(100vw-5rem))] max-w-[min(1480px,calc(100vw-5rem))] rounded-2xl border border-default-200 shadow-2xl">
          <ModalHeader className="px-6 pt-6 pb-3">
            <div className="flex flex-col">
              <span>Editar usuario</span>
              <span className="text-xs font-normal text-default-400">{selected?.email || selected?.id}</span>
            </div>
          </ModalHeader>
          <ModalBody className="min-h-0 overflow-auto px-6 py-3">
            {jsonData == null ? (
              <div className="flex justify-center p-8"><Spinner label="Cargando campos..." /></div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 pb-3">
                  {canWrite && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-default-200 bg-content1 p-2">
                      <input
                        className="h-9 w-56 rounded-md border border-default-200 bg-default-100 px-3 text-sm text-foreground outline-none transition focus:border-primary"
                        placeholder="nuevoCampo o mapa.subcampo"
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') addJsonField();
                        }}
                      />
                      <select
                        className="h-9 rounded-md border border-default-200 bg-default-100 px-2 text-sm text-foreground outline-none transition focus:border-primary"
                        value={newFieldType}
                        onChange={(e) => setNewFieldType(e.target.value)}
                      >
                        <option value="string">texto</option>
                        <option value="number">numero</option>
                        <option value="boolean">booleano</option>
                        <option value="timestamp">fecha</option>
                        <option value="map">map</option>
                      </select>
                      <Button
                        size="sm"
                        variant="flat"
                        color="success"
                        onPress={addJsonField}
                        isDisabled={!newFieldName.trim()}
                      >
                        Añadir campo
                      </Button>
                    </div>
                  )}
                  {canWrite && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-default-200 bg-content1 p-2">
                      <span className="text-xs font-semibold text-default-600">Fecha fin del plan</span>
                      <input
                        type="datetime-local"
                        className="h-9 rounded-md border border-default-200 bg-default-100 px-3 text-sm text-foreground outline-none transition focus:border-primary"
                        value={timestampInputValue(jsonData.planEnd)}
                        onChange={(e) => updateJsonField(
                          ['planEnd'],
                          e.target.value
                            ? { __type: 'timestamp', value: new Date(e.target.value).toISOString() }
                            : null
                        )}
                      />
                      {jsonData.planEnd != null && (
                        <Button
                          size="sm"
                          variant="flat"
                          color="danger"
                          onPress={() => updateJsonField(['planEnd'], null)}
                        >
                          Quitar
                        </Button>
                      )}
                    </div>
                  )}
                  {!isPlainObject(jsonData.ambassador) && (
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      onPress={() => ensureJsonMap('ambassador', { active: true, language: 'ES' })}
                    >
                      Crear map Ambassador
                    </Button>
                  )}
                  {!isPlainObject(jsonData.founder) && (
                    <Button
                      size="sm"
                      variant="flat"
                      color="secondary"
                      onPress={() => ensureJsonMap('founder', { isFounder: true, active: true, number: 1 })}
                    >
                      Crear map Founder
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 pb-2 md:grid-cols-2 xl:grid-cols-4">
                  {renderFirestoreFields(jsonData)}
                </div>
              </>
            )}
            <p className="text-xs text-default-500">
              Guarda reemplaza el documento users/{'{uid}'}. Los maps se muestran agrupados y los campos complejos se pueden editar como JSON parcial. Los campos añadidos no se escriben en Firestore hasta pulsar Guardar cambios.
            </p>
            {jsonError && (
              <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                {jsonError}
              </div>
            )}
          </ModalBody>
          <ModalFooter className="px-6 pt-3 pb-6">
            <Button variant="flat" onPress={jsonModal.onClose} isDisabled={savingJson}>Cancelar</Button>
            <Button color="primary" onPress={saveJson} isLoading={savingJson}>Guardar cambios</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose}>
        <ModalContent>
          <ModalHeader>Eliminar usuario</ModalHeader>
          <ModalBody>
            <p className="text-sm">
              Vas a eliminar la cuenta <code>{selected?.id || selected?.uid}</code> por completo: el documento <code>users/{'{uid}'}</code>, su cuenta de Firebase Auth y todos sus datos en la nube (perfiles, configuraciones de widgets, puntos, batallas, presets, archivos subidos y el reenganche por email). Si vuelve a registrarse con el mismo correo, empezara de cero.
            </p>
            <p className="text-sm text-danger">Esta accion no se puede deshacer.</p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={deleteModal.onClose} isDisabled={deleting}>Cancelar</Button>
            <Button color="danger" onPress={deleteUser} isLoading={deleting}>Eliminar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={paymentNoticesModal.isOpen}
        onClose={paymentNoticesModal.onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex flex-col">
              <span>Avisos de impago pendientes</span>
              <span className="text-xs font-normal text-default-400">
                {paymentNoticePreview?.candidates || 0} usuarios encontrados
                {paymentNoticePreview?.founders ? ` · ${paymentNoticePreview.founders} Founders` : ''}
              </span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
              Se crearán links de pago pendientes y se enviará el email correspondiente. Los usuarios con aviso abierto no se incluyen.
            </div>
            <div className="max-h-[55vh] overflow-auto rounded-lg border border-default-200">
              <table className="min-w-full text-sm">
                <thead className="bg-default-100 text-default-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Usuario</th>
                    <th className="px-3 py-2 text-left font-medium">Plan</th>
                    <th className="px-3 py-2 text-left font-medium">Estado</th>
                    <th className="px-3 py-2 text-left font-medium">Founder</th>
                    <th className="px-3 py-2 text-right font-medium">Stripe</th>
                    <th className="px-3 py-2 text-left font-medium">Suscripción</th>
                  </tr>
                </thead>
                <tbody>
                  {(paymentNoticePreview?.users || []).map((user) => (
                    <tr key={user.uid || user.stripeSubscriptionId} className="border-t border-default-100">
                      <td className="px-3 py-2">
                        <div className="flex min-w-[220px] flex-col">
                          <span>{user.email || '-'}</span>
                          <code className="break-all text-xs text-default-400">{user.uid || '-'}</code>
                        </div>
                      </td>
                      <td className="px-3 py-2">{formatPlan(user.plan)}</td>
                      <td className="px-3 py-2">
                        <Chip size="sm" color="warning" variant="flat">{user.stripeStatus || '-'}</Chip>
                      </td>
                      <td className="px-3 py-2">
                        {user.isFounder ? (
                          <Chip size="sm" color="secondary" variant="flat">
                            {user.founderNumber ? `#${user.founderNumber}` : 'Founder'}
                          </Chip>
                        ) : (
                          <span className="text-default-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {stripeDashboardUrl(user) ? (
                          <Button
                            as="a"
                            href={stripeDashboardUrl(user)}
                            target="_blank"
                            rel="noreferrer"
                            size="sm"
                            variant="flat"
                            color="secondary"
                          >
                            Abrir
                          </Button>
                        ) : (
                          <span className="text-default-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <code className="break-all text-xs text-default-500">{user.stripeSubscriptionId || '-'}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={paymentNoticesModal.onClose} isDisabled={repairingPaymentNotices}>
              Cancelar
            </Button>
            <Button color="warning" onPress={sendPaymentNotices} isLoading={repairingPaymentNotices}>
              Enviar {paymentNoticePreview?.candidates || 0} avisos
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
